import type { TypeaheadAdapter } from '@app/adapters/types'

import { SfLog } from '@app/adapters/logger'
import sanitizer from '@app/adapters/sanitizer'
import SFetch from '@app/utils/sfetch'
import { nextAll } from '@app/utils/domHelpers'

interface AsyncAutoCompleteInterface {
  destroyCallback: () => void
}

type Ids = Record<string, string>

export default class AsyncAutoComplete implements AsyncAutoCompleteInterface {
  destroyCallback: () => void

  constructor({
    element,
    path,
    typeahead,
  }: {
    element: HTMLInputElement
    path: string
    typeahead: TypeaheadAdapter
  }) {
    const ids: Ids = {}

    const params: { filter?: string; q?: string } = {}

    const filter = element.dataset.filter

    if (filter) {
      params.filter = filter
    }

    this.destroyCallback = typeahead(element, {
      items: 15,
      source(query, process) {
        params['q'] = query

        const target = new URL(path, document.baseURI)
        target.search = new URLSearchParams(params).toString()

        AsyncAutoComplete.fetchData({ target })
          .then((result) => {
            if (
              process &&
              typeof process === 'function' &&
              result &&
              result.names
            ) {
              process(result.names)

              Object.assign(ids, result.ids)
            }
          })
          .catch((e) => {
            SfLog.warn('Error fetching data for autocomplete', {
              extra: {
                error: e,
              },
            })
          })
      },

      updater(itemName) {
        const allNext = nextAll(element, 'input')
        const nextInput = allNext[0] as HTMLInputElement

        if (nextInput) {
          nextInput.value = ids[itemName] || ''
          nextInput.dispatchEvent(new Event('change'))

          element.insertAdjacentHTML('afterend', itemName)

          const parentNode = element.parentNode

          if (parentNode) {
            element.parentNode.removeChild(element)
          }

          return nextInput.value
        } else {
          return ''
        }
      },
    })
  }

  static async fetchData({
    target,
  }: {
    target: URL
  }): Promise<{ names: string[]; ids: Ids } | undefined> {
    const data = await SFetch.json<{ name: string; id: string }[] | undefined>(
      target
    )
    const names: string[] = []
    const ids: Ids = {}

    if (data) {
      data.forEach((item) => {
        const name = sanitizer(item.name, {
          USE_PROFILES: { html: false },
        }) as string
        ids[name] = item.id
        names.push(name)
      })
    }

    return { names, ids }
  }
}
